/**
 * MotivationCard Component
 * Displays motivational content alongside the Activity Heatmap
 * Shows streak progress, encouraging messages, and CTAs
 */

import { useNavigate } from 'react-router-dom'
import { Flame, Rocket, CheckCircle, Trophy, Target, ChevronRight } from 'lucide-react'
import { getMotivationMessage, getNextMilestone, getCardState } from './motivationUtils'

/**
 * MotivationCard - Companion card for ActivityHeatmap
 * Inspires users to stay active with streak-based messaging
 *
 * @param {Object} props - Component props
 * @param {Object} props.stats - Activity stats from useActivityHeatmap
 * @param {string} props.className - Additional CSS classes
 */
function MotivationCard({ stats = {}, className = '' }) {
  const navigate = useNavigate()
  const {
    currentStreak = 0,
    longestStreak = 0,
    isActiveToday = false
  } = stats

  const cardState = getCardState(currentStreak, longestStreak, isActiveToday)
  const message = getMotivationMessage(currentStreak, longestStreak, isActiveToday)
  const nextMilestone = getNextMilestone(currentStreak)

  // Calculate progress percentage toward longest streak or next milestone
  const progressTarget = longestStreak > 0 ? longestStreak : nextMilestone
  const progress = progressTarget > 0 ? Math.min((currentStreak / progressTarget) * 100, 100) : 0

  // Determine icon and styling based on card state
  const getCardConfig = () => {
    switch (cardState) {
      case 'milestone':
        return {
          icon: Trophy,
          iconBg: 'bg-gradient-to-br from-purple-500 to-pink-500',
          iconColor: 'text-white',
          title: 'Milestone Achieved!',
          badgeText: `${currentStreak} day streak`,
          badgeBg: 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30',
          badgeText2: 'text-purple-700 dark:text-purple-400',
          progressBg: 'bg-gradient-to-r from-purple-400 to-pink-500'
        }
      case 'active-today':
        return {
          icon: CheckCircle,
          iconBg: 'bg-gradient-to-br from-green-500 to-emerald-500',
          iconColor: 'text-white',
          title: 'Great Job Today!',
          badgeText: currentStreak > 0 ? `${currentStreak} day streak` : 'Active today',
          badgeBg: 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30',
          badgeText2: 'text-green-700 dark:text-green-400',
          progressBg: 'bg-gradient-to-r from-green-400 to-emerald-500'
        }
      case 'has-streak':
        return {
          icon: Flame,
          iconBg: 'bg-gradient-to-br from-orange-500 to-red-500',
          iconColor: 'text-white',
          title: 'Keep Your Streak Alive!',
          badgeText: `${currentStreak} day streak`,
          badgeBg: 'bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30',
          badgeText2: 'text-orange-700 dark:text-orange-400',
          progressBg: 'bg-gradient-to-r from-orange-400 to-red-500'
        }
      default: // no-streak
        return {
          icon: Rocket,
          iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-500',
          iconColor: 'text-white',
          title: 'Start Your Streak Today!',
          badgeText: 'No active streak',
          badgeBg: 'bg-gray-100 dark:bg-gray-800',
          badgeText2: 'text-gray-600 dark:text-gray-400',
          progressBg: 'bg-gray-300 dark:bg-gray-600'
        }
    }
  }

  const config = getCardConfig()
  const Icon = config.icon

  // Determine CTA buttons based on state
  const getPrimaryCTA = () => {
    if (cardState === 'no-streak') {
      return { label: 'Start a Challenge', path: '/challenge-type' }
    }
    if (cardState === 'active-today') {
      return { label: 'See Progress', path: '/progress' }
    }
    return { label: 'Continue Practicing', path: '/challenge-type' }
  }

  const getSecondaryCTA = () => {
    if (cardState === 'active-today') {
      return { label: 'Keep Going', path: '/challenge-type' }
    }
    return { label: 'View Progress', path: '/progress' }
  }

  const primaryCTA = getPrimaryCTA()
  const secondaryCTA = getSecondaryCTA()

  return (
    <div className={`card ${className}`}>
      <div className="card-body space-y-4">
        {/* Header with icon and title */}
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl shadow-md ${config.iconBg}`}>
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {config.title}
          </h3>
        </div>

        {/* Streak progress visualization */}
        <div className={`p-3 rounded-lg ${config.badgeBg}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${config.badgeText2}`}>
              {config.badgeText}
            </span>
            {longestStreak > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {longestStreak} best
              </span>
            )}
          </div>
          {/* Progress bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${config.progressBg}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          {currentStreak > 0 && currentStreak < nextMilestone && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              {nextMilestone - currentStreak} more {nextMilestone - currentStreak === 1 ? 'day' : 'days'} to {nextMilestone}-day milestone
            </p>
          )}
        </div>

        {/* Motivational message */}
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          "{message}"
        </p>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => navigate(primaryCTA.path)}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow-md transition-all"
          >
            <Target className="w-4 h-4" />
            <span>{primaryCTA.label}</span>
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate(secondaryCTA.path)}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
          >
            {secondaryCTA.label}
          </button>
        </div>
      </div>
    </div>
  )
}

export default MotivationCard
